import { arrowTagPattern, CORPORATE, INDIVIDUAL } from '@Utils/constants';
import { bank_validations, beneSchemaValidation, validateRoutingValue } from '@Utils/validation';
import { get, isEmpty } from 'lodash';
import {
  beneficiaryFieldsForINvalidation,
  PAYTM_UPI_ID_ERROR_MESSAGE
} from '../beneficiary-info/constant';
import { encryptDataWithSignature } from '@Utils/library';
import { change, touch, untouch } from 'redux-form';
import { makeApiCall } from '@Redux/actions/common';
import { API_URL, API_URL_V3, BE_BASE_URL } from '@Utils/config';
import { ASYNC_VALIDATION_RESPONSE } from '@Redux/action-contants';
import { handleError, showMessage } from '@Redux/actions/common-message';
import { AppDispatch } from '../../store.type';
import { ADDRESS_FIELDS } from '../payee-details/payee_details_initializer';
import { removeV3Fields } from '@Redux/actions/individual-user-flow/individual-user-flow-action';
import {
  BeneficiaryConfigDataType,
  GenericRecordType,
  GetStateFn,
  IBeneInfoFormState
} from './interface';
import { store } from '../../store';

export const FALLBACK_DATA = {
  heading: `{RECIPIENT_NAME} information required`,
  nameDescription:
    'Enter the recipient’s name exactly as it appears on their bank account to avoid delays or incorrect transfers.',
  bankDetailsDescription:
    'Enter the correct recipient bank details to avoid delays or failed transfers.'
};

export const BENE_INFO_CORRECTION_FORM = 'BeneInfoCorrectionForm';
export const BENE_IDENTITY_HELPER_TEXT =
  'Recipient’s Passport, Driver’s License, or National ID number';
export const BENE_NAME_HELPER_TEXT = `Enter the recipient’s full name as per their bank records to avoid transfer delays or rejection. 
If the bank records use initials, enter the full name instead.`;

export const ASYNC_BLUR_FIELDS = [
  'routing_code_value_1',
  'bank_account_number',
  'card_number',
  'routing_code_value_2',
  'beneficiary_account_number',
  'proxy_value'
];

export const MSG_BANK_CHANNEL = `Card is enabled for bank channel, Please note your recipient has
  to register on Geoswift & accept the transaction to receive the transfer.`;

export const MSG_MONEY_EXPRESS =
  'Card is enabled for express service, your recipient can receive money instantly.';

export const MSG_FAST_FUNDS_YES =
  'Recipient will receive funds within 30 minutes after your transfer';

export const MSG_FAST_FUNDS_NO =
  'This card is not enabled for instant transfers. Recipient will receive funds in 2 days after your transfer';

export const validate =
  (
    getvalidationConfig: () => {
      beneficiary_bank_validations: GenericRecordType;
      bene_schema_validation: GenericRecordType;
    },
    reduxData: GenericRecordType
  ) =>
  (values: GenericRecordType) => {
    let errors: any = {};
    const { bank_account_country, country_of_business, currency_code, account_type } =
      reduxData || {};
    const bank_account_country_code = bank_account_country || country_of_business;
    let { beneficiary_bank_validations, bene_schema_validation } = getvalidationConfig();
    if (beneficiary_bank_validations && values) {
      errors = bank_validations({ ...values, currency_code }, beneficiary_bank_validations);
    }
    if (!isEmpty(bene_schema_validation) && values) {
      const checkForINR =
        bank_account_country_code === 'IN' &&
        currency_code === 'INR' &&
        account_type === INDIVIDUAL;
      const checkForPaytmUPI =
        values.proxy_type === 'VPA' && values.proxy_value?.toLowerCase().includes('@paytm');
      let schema = bene_schema_validation;
      if (checkForINR) {
        schema = {
          ...bene_schema_validation,
          personal_details: {
            ...beneficiaryFieldsForINvalidation(values.is_legal_last_name_provided),
            ...bene_schema_validation.personal_details
          }
        };
        if (checkForPaytmUPI) {
          schema = {
            ...schema,
            payout_details: {
              ...schema.payout_details,
              proxy_value: {
                ...schema.payout_details.proxy_value,
                validation: {
                  ...schema.payout_details.proxy_value.validation,
                  error_message: PAYTM_UPI_ID_ERROR_MESSAGE
                }
              }
            }
          };
        }
      }
      errors = beneSchemaValidation(
        { ...values, bank_account_country, country_of_business, currency_code, account_type },
        schema
      );
    }
    return errors;
  };

export const asyncValidate =
  (reduxData: GenericRecordType) => (values: GenericRecordType, dispatch: AppDispatch) => {
    return new Promise((resolve, reject) => {
      const validationArray: any = [];
      const fieldnames: any = [];
      const { bank_account_country, country_of_business, currency_code, account_type, formName } =
        reduxData || {};
      const country_code = bank_account_country || country_of_business;
      const checkForINR =
        bank_account_country === 'IN' && currency_code === 'INR' && account_type === INDIVIDUAL;
      const checkForPaytmUPI =
        values.proxy_type === 'VPA' && values.proxy_value?.toLowerCase().includes('@paytm');
      const isIndividualChinaBeneWithCNYCurrency =
        account_type === INDIVIDUAL && country_code === 'CN' && currency_code === 'CNY';

      const reduxStore = store.getState();
      const { selectedConfigBeneValidation, selectedSchemaBeneValidation } = get(
        reduxStore,
        'rootReducer.transactionReducer',
        {}
      );

      // bank_card_validations is set by BankDetailsBlockSG
      const beneficiary_bank_validations =
        values['bank_card_validations'] || selectedConfigBeneValidation;
      //bene_schema_validation is set by Beneficiary-info-v2
      const bene_schema_validation =
        values['bene_schema_validation'] || selectedSchemaBeneValidation;

      if (!isEmpty(bene_schema_validation)) {
        Object.keys(bene_schema_validation).forEach((field) => {
          const api_validation = bene_schema_validation[field].render?.api_validation;
          if (
            !isEmpty(api_validation) &&
            !values[bene_schema_validation[field].render.value_field_name + '_disabled'] &&
            !(checkForINR && checkForPaytmUPI)
          ) {
            const body: any = {};
            const paramsObj: any = api_validation.body || api_validation.params;
            Object.keys(paramsObj).forEach((key) => {
              body[key] = bene_schema_validation[field].render?.label?.includes('IFSC')
                ? values[paramsObj[key]].toUpperCase() || ''
                : values[paramsObj[key]] || '';
            });

            if (!isEmpty(api_validation.encryption)) {
              const encriptedCardData = encryptDataWithSignature(body[api_validation.encryption]);
              body[api_validation.encryption] = encriptedCardData.encryptedData;
              body.signature = encriptedCardData.signature;
            }
            dispatch(
              change(
                formName,
                bene_schema_validation[field].render.value_field_name + '_success',
                false
              )
            );
            const apiData = api_validation.body
              ? {
                  data: body
                }
              : {
                  params: body
                };
            const response = makeApiCall({
              url: BE_BASE_URL + api_validation.version + api_validation.url,
              method: api_validation.method,
              withCredentials: true,
              ...apiData
            });
            validationArray.push(response);
            fieldnames.push({ field_name: bene_schema_validation[field].render.value_field_name });
          }
        });
      }
      beneficiary_bank_validations &&
        Object.keys(beneficiary_bank_validations).forEach((key) => {
          if (
            beneficiary_bank_validations[key] &&
            beneficiary_bank_validations[key] instanceof Object
          ) {
            let obj = beneficiary_bank_validations[key];
            let field_name = obj['render']['value_field_name'];
            let isSortCode = country_code === 'GB' && currency_code === 'GBP';
            let isCanadaAndCad = country_code === 'CA' && currency_code === 'CAD';
            const isSearchList = obj['render']['type'] === 'searchList';
            const isKenyaWallet = values['details_selected'] === 'wallet' && country_code === 'KE';

            if (field_name in values) {
              if (obj['url'] && values[field_name] && !isSearchList) {
                if (obj['render']['label'].toLowerCase() === 'iban') {
                  validationArray.push(
                    validateRoutingValue(
                      {
                        bank_account_number: values[field_name]
                      },
                      obj['url'],
                      obj['method']
                    )
                  );
                  fieldnames.push({ field_name, label: obj['render']['label'].toLowerCase() });
                } else if (
                  !isCanadaAndCad &&
                  isSortCode &&
                  values['routing_code_value_1'] &&
                  values['bank_account_number']
                ) {
                  validationArray.push(
                    validateRoutingValue(
                      {
                        routing_code_value_1: values['routing_code_value_1'],
                        bank_account_number: values['bank_account_number'],
                        country_code
                      },
                      obj['url'],
                      obj['method']
                    )
                  );
                  fieldnames.push({ field_name, label: 'Sort Code' });
                } else if (
                  !isSortCode &&
                  isCanadaAndCad &&
                  values['routing_code_value_1'] &&
                  values['routing_code_value_2']
                ) {
                  validationArray.push(
                    validateRoutingValue(
                      {
                        routing_code_value_1: values['routing_code_value_1'],
                        routing_code_value_2: values['routing_code_value_2'],
                        country_code
                      },
                      obj['url'],
                      obj['method'] || 'GET'
                    )
                  );
                  fieldnames.push({ field_name, label: obj['render']['label'].toLowerCase() });
                } else if (
                  obj['render']['value_field_name'].toLowerCase() === 'card_number' &&
                  values.is_card_number_updated !== false
                ) {
                  const encriptedCardData = encryptDataWithSignature(values[field_name]);
                  let validationObject = {
                    card_number: encriptedCardData.encryptedData,
                    country_code: country_code,
                    currency_code: currency_code,
                    signature: encriptedCardData.signature
                  };
                  if (isIndividualChinaBeneWithCNYCurrency) {
                    delete validationObject.country_code;
                    delete validationObject.currency_code;
                  }
                  validationArray.push(
                    validateRoutingValue(validationObject, obj['url'], obj['method'] || 'GET')
                  );
                  fieldnames.push({
                    field_name,
                    label: obj['render']['value_field_name'].toLowerCase()
                  });
                } else if (
                  !isSortCode &&
                  !isCanadaAndCad &&
                  obj['render']['value_field_name'].toLowerCase() !== 'card_number' &&
                  !isKenyaWallet
                ) {
                  validationArray.push(
                    validateRoutingValue(
                      {
                        routing_code_type_1:
                          beneficiary_bank_validations[obj['render']['type_field_name']],
                        routing_code_value_1: values[field_name],
                        country_code: country_code
                      },
                      obj['url'],
                      obj['method']
                    )
                  );
                  fieldnames.push({ field_name, label: obj['render']['label'].toLowerCase() });
                }
              }
            }
          }
        });

      Promise.all(validationArray)
        .then((result) => {
          const errors: any = {};
          result.forEach((key, index) => {
            /** store in 'bankValidationsReducer' response from validation api */
            try {
              const {
                data: { data }
              } = key;
              dispatch({ type: ASYNC_VALIDATION_RESPONSE, data });
            } catch (e) {
              // eslint-disable-next-line no-console
              console.log('redux value not set for async response', e);
            }

            if (!isEmpty(bene_schema_validation)) {
              const currField: any = Object.keys(bene_schema_validation).find(
                (fieldKey) =>
                  bene_schema_validation[fieldKey].render?.value_field_name ===
                  fieldnames[index].field_name
              );
              const onSuccess =
                bene_schema_validation[currField]?.render?.api_validation?.on_success || false;
              if (result[index]?.data?.success) {
                if (onSuccess) {
                  onSuccess.forEach((element: any) => {
                    dispatch(
                      change(
                        formName,
                        element['populate'],
                        result[index].data.data[element['node']]
                      )
                    );
                  });
                }
                dispatch(change(formName, fieldnames[index].field_name + '_success', true));
              } else {
                errors[fieldnames[index].field_name] = result[index].data.message;
                if (onSuccess) {
                  onSuccess.forEach((element: any) => {
                    dispatch(change(formName, element['populate'], '', false));
                  });
                }
                dispatch(change(formName, fieldnames[index].field_name + '_success', false));
              }
            } else {
              if (result[index]?.data?.success) {
                const data = result[index].data.data;
                dispatch({ type: 'BANK_DETAILS_SUCCESS', data: { show_loader: false } });
                if (fieldnames[index].label === 'iban') {
                  if (data.address.country_code !== country_code) {
                    errors[fieldnames[index].field_name] = `Invalid ${fieldnames[index].label}`;
                  } else {
                    let isPakuser = country_code === 'PK' && currency_code === 'PKR';
                    if (!isPakuser) {
                      dispatch(change(formName, 'bank_name', data.institution_name));
                      dispatch(change(formName, 'routing_code_value_1', data.bic));
                      dispatch({ type: 'BANK_DETAILS_SUCCESS', data: { bic: data.bic } });
                    }
                  }
                } else if (values['routing_code_value_1'] && values['routing_code_value_2']) {
                  if (data.bankName || data.institution_name) {
                    dispatch(change(formName, 'bank_name', data.institution_name));
                  } else {
                    errors[fieldnames[index].field_name] = `Invalid ${fieldnames[index].label}`;
                  }
                } else if (
                  fieldnames[index].label &&
                  fieldnames[index].label.replace(/ |\n/g, '').toUpperCase() === 'SORTCODE' &&
                  data
                ) {
                  if ('IsCorrect' in data) {
                    if (
                      data['IsCorrect'] &&
                      ['OK', 'CAUTIOUSOK'].includes(
                        data.StatusInformation && data.StatusInformation.trim().toUpperCase()
                      )
                    ) {
                      if (
                        data.CorrectedSortCode &&
                        data.CorrectedSortCode !== values['routing_code_value_1']
                      ) {
                        errors[
                          'routing_code_value_1'
                        ] = `corrected sort code ${data.CorrectedSortCode}`;
                      } else dispatch(change(formName, 'bank_name', data.institution_name));
                    } else {
                      if (data.StatusInformation.toUpperCase() === 'INVALIDACCOUNTNUMBER') {
                        errors['bank_account_number'] = 'Incorrect bank account number';
                      } else {
                        errors['routing_code_value_1'] = 'Invalid SORT code';
                      }
                      if ('bank_name' in values) {
                        dispatch(change(formName, 'bank_name', ''));
                        dispatch(untouch(formName, 'bank_name'));
                        dispatch(untouch(formName, fieldnames[index].field_name));
                      }
                    }
                  } else {
                    errors['routing_code_value_1'] = 'Invalid SORT code';
                  }
                  dispatch(touch(formName, 'routing_code_value_1', 'bank_account_number'));
                } else if (fieldnames[index].label === 'card_number') {
                  if (isIndividualChinaBeneWithCNYCurrency) {
                    dispatch(change(formName, 'card_issuer_name', data.bankName));
                    dispatch(change(formName, 'payment_mode_supported', data.paymentModeSupported));
                    if (data.paymentModeSupported === 'BANK_CHANNEL')
                      dispatch(change(formName, 'fast_fund_message', MSG_BANK_CHANNEL));
                    else if (data.paymentModeSupported === 'MONEY_EXPRESS')
                      dispatch(change(formName, 'fast_fund_message', MSG_MONEY_EXPRESS));
                  } else {
                    dispatch(change(formName, 'show_visa_icon', true));
                    if (data.fastFundsIndicator === 'Y')
                      dispatch(change(formName, 'fast_fund_message', MSG_FAST_FUNDS_YES));
                    else if (data.fastFundsIndicator === 'N')
                      dispatch(change(formName, 'fast_fund_message', MSG_FAST_FUNDS_NO));
                  }
                } else {
                  // check swift code is of same country
                  if (data.address.country_code !== country_code) {
                    errors[fieldnames[index].field_name] = `Invalid ${fieldnames[index].label}`;
                    if ('bank_name' in values) {
                      dispatch(change(formName, 'bank_name', ''));
                      dispatch(untouch(formName, 'bank_name'));
                      dispatch(untouch(formName, fieldnames[index].field_name));
                    }
                  } else {
                    if (data.bic) {
                      dispatch(change(formName, fieldnames[index].field_name, data.bic));
                    }
                    dispatch(change(formName, 'bank_name', data.institution_name));
                  }
                }
                dispatch(change(formName, 'beneficiary_bank_address_info', data.address));
              } else {
                if (fieldnames[index].label === 'card_number') {
                  errors[fieldnames[index].field_name] = result[index].data.message;
                } else if (values['routing_code_value_1'] && values['routing_code_value_2']) {
                  errors['routing_code_value_2'] = result[index].data.message;
                } else {
                  errors[fieldnames[index].field_name] = `Invalid ${fieldnames[
                    index
                  ].label.toUpperCase()}`;
                }
                if ('bank_name' in values) {
                  dispatch(change(formName, 'bank_name', ''));
                  dispatch(untouch(formName, 'bank_name'));
                }
              }
            }
          });

          if (Object.keys(errors).length === 0) {
            resolve(undefined);
          } else {
            reject(errors);
          }
        })
        .catch((err) => {
          if (err?.response?.data?.data?.message) {
            dispatch(showMessage({ text: err.response.data.data.message, type: 'info' }));
          } else {
            dispatch(showMessage({ text: ['Something went wrong'], type: 'info' }));
          }
          reject(err);
        });
    });
  };

export const getPayoutValidation = (
  payoutMethod: string,
  debit_validations: GenericRecordType | undefined,
  bank_validations: GenericRecordType | undefined,
  wallet_validations: GenericRecordType | undefined
) => {
  switch (payoutMethod) {
    case 'card':
      return debit_validations;
    case 'bank':
      return bank_validations;
    case 'wallet':
      return wallet_validations;
    default:
      return null;
  }
};

const CONFIG_FIELDS_TO_REMOVE = [
  'disable_trade_name',
  'street',
  'social_credit_code',
  'organization_code',
  'bank_card_validations',
  'bank_account',
  'debit_card',
  'show_visa_icon',
  'beneficiary_routing_config_id_bank',
  'beneficiary_routing_config_id_debit',
  'fast_fund_message',
  'payment_mode_supported',
  'card_type',
  'bank_details_type',
  'routing_config_type',
  'details_selected',
  'remitter_id',
  'full_name',
  'payout_method',
  'routing_code_type_1',
  'is_account_detail_submitted',
  'beneficiary_group_id',
  'relationship_code',
  'is_legal_last_name_provided',
  'is_completely_editable',
  'is_partially_editable',
  'is_validate_vop_enable',
  'routing_code_type_2',
  'beneficiary_name',
  'account_verification_status'
];

const SCHEMA_FIELDS_TO_REMOVE = [
  'account_type',
  'bank_account_country',
  'currency_code',
  'mobile_country_code',
  'details_selected',
  'bene_schema_validation',
  'routing_config_type_temp',
  'is_legal_last_name_provided',
  'is_validate_vop_enable',
  'full_name',
  'is_account_detail_submitted',
  'account_verification_status'
];

export const removeUnwantedFields = (payload: GenericRecordType, FIELDS_TO_REMOVE: string[]) => {
  const result = { ...payload };
  FIELDS_TO_REMOVE.forEach((field) => {
    delete result[field];
  });
  return result;
};

export function sanitizeFields(payload: GenericRecordType) {
  return Object.fromEntries(
    Object.entries(payload).filter(
      ([_, value]) => value !== null && value !== undefined && value !== ''
    )
  );
}

export const removeAddressFields = (payload: GenericRecordType) => {
  const result = { ...payload };
  ADDRESS_FIELDS.forEach((field) => {
    delete result[field];
  });
  return result;
};

export const addNameFields = (postData: GenericRecordType) => {
  const beneFiciaryName = postData?.beneficiary_name;
  if (isEmpty(beneFiciaryName)) return;

  const nameArray = beneFiciaryName.split(' ');
  postData.first_name = nameArray[0];
  if (nameArray.length > 2) {
    postData.middle_name = nameArray[1];
    postData.last_name = nameArray[2];
  } else if (nameArray.length === 2) {
    postData.middle_name = null;
    postData.last_name = nameArray[1];
  } else {
    postData.middle_name = null;
    postData.last_name = null;
  }
};

export const getSanitizedBeneConfigDetails = (
  data: BeneficiaryConfigDataType,
  state: IBeneInfoFormState,
  getState: GetStateFn
) => {
  const isIndividualChinaBeneWithCNYCurrency =
    data.bank_account_country === 'CN' &&
    data.currency_code === 'CNY' &&
    data.account_type === INDIVIDUAL;

  const { config_id, debit_config_id, cash_payout_config, wallet_config } = state;

  const { details_selected, is_card_number_updated } = data;
  const beneficiary_routing_config_id =
    details_selected === 'cash' || details_selected === 'money_cash'
      ? cash_payout_config
      : details_selected === 'card'
      ? debit_config_id
      : details_selected === 'wallet'
      ? wallet_config
      : config_id;
  if ((details_selected === 'card' || isIndividualChinaBeneWithCNYCurrency) && data.card_number) {
    const encriptedCardData = encryptDataWithSignature(data.card_number);
    data.card_number = encriptedCardData.encryptedData;
    data.signature = encriptedCardData.signature;
  }
  if (details_selected === 'card') {
    data.is_card_number_updated = is_card_number_updated;
  }
  const postData: BeneficiaryConfigDataType = {
    ...data,
    account_type: data.account_type?.toUpperCase()
  };

  postData.bank_account_number && delete postData.card_number;
  postData.card_number && delete postData.bank_account_number;
  postData.card_number && delete postData.bank_name;
  !postData.routing_code_value_1 && delete postData.routing_code_value_1;
  !postData.routing_code_value_2 && delete postData.routing_code_value_2;
  !postData.routing_code_value_3 && delete postData.routing_code_value_3;
  //@ts-ignore
  postData.beneficiary_routing_config_id = beneficiary_routing_config_id;

  removeV3Fields(postData);

  Object.keys(postData).forEach((key) => {
    if (postData[key] === null || postData[key] === undefined || postData[key] === '') {
      // us usd bene email mobile exempted from sanitization because they are optional so needs to be updated blank
      const isUSUSDBeneEmailMobile =
        ['email', 'mobile_number'].includes(key) &&
        postData.bank_account_country === 'US' &&
        postData.currency_code === 'USD';
      if (!isUSUSDBeneEmailMobile) {
        delete postData[key];
      }
    }
  });
  const { country_code, account_type } = getState().rootReducer.userStatusReducer;
  const user_account_type = account_type;
  //Added country_code to null as country of residence is removed for Corporate Account
  if (
    user_account_type !== CORPORATE &&
    postData['account_type'] === CORPORATE &&
    ['SG'].includes(country_code)
  ) {
    postData['country_code'] = null;
  }
  postData.is_vop_beneficiary_update = true;
  addNameFields(postData);
  const filteredFields = removeUnwantedFields(postData, CONFIG_FIELDS_TO_REMOVE);
  return filteredFields;
};

export const updateConfigBeneDetails = (
  data: GenericRecordType,
  beneficiary_id: number,
  state: IBeneInfoFormState
) => {
  return async (dispatch: AppDispatch, getState: GetStateFn) => {
    try {
      const payload = getSanitizedBeneConfigDetails(data, state, getState);
      const response = await makeApiCall({
        method: 'patch',
        url: `${API_URL}/remitter/beneficiary/${beneficiary_id}`,
        withCredentials: true,
        data: payload
      });
      return response;
    } catch (error) {
      dispatch(handleError(error));
    }
  };
};

export const getSanitizedBeneSchemaDetails = (data: GenericRecordType) => {
  const { beneficiary_bank_name } = data;
  let propsCpy = {
    ...data
  };
  if (beneficiary_bank_name) {
    propsCpy.beneficiary_bank_name = beneficiary_bank_name;
  }

  let encryptField;
  if (propsCpy.bene_schema_validation) {
    Object.keys(propsCpy.bene_schema_validation).forEach((field) => {
      const encryption =
        propsCpy?.bene_schema_validation[field]?.render?.api_validation?.encryption;
      if (!isEmpty(encryption)) encryptField = encryption;
    });
  }
  if (isEmpty(propsCpy.routing_config_type)) {
    propsCpy.routing_config_type = propsCpy.routing_config_type_temp;
  }
  if (encryptField && !propsCpy[encryptField + '_disabled']) {
    const encriptedCardData = encryptDataWithSignature(propsCpy[encryptField]);
    propsCpy[encryptField] = encriptedCardData.encryptedData;
    propsCpy.signature = encriptedCardData.signature;
    //if user edited card_number need to remove card_token
    delete propsCpy.card_token;
  } else if (propsCpy.card_number && propsCpy[encryptField + '_disabled']) {
    //if user haven't edited card no dont send card no
    //card_token is enough
    delete propsCpy.card_number;
  }
  Object.keys(propsCpy).forEach((field) => {
    // to remove BicCODE from the bank name
    if (arrowTagPattern.test(propsCpy[field])) {
      propsCpy[field] = propsCpy[field].split(arrowTagPattern)[0];
    }
    if (field.endsWith('_success') || field.endsWith('_disabled')) delete propsCpy[field];
  });
  const postData = { ...propsCpy };
  postData.destination_country = postData.bank_account_country;
  postData.destination_currency = postData.currency_code;
  postData.payout_method = postData.details_selected;
  postData.is_vop_beneficiary_update = true;
  addNameFields(postData);

  const filteredFields = removeUnwantedFields(postData, SCHEMA_FIELDS_TO_REMOVE);
  return filteredFields;
};

export const updateSchemaEnabledBeneDetails = (data: GenericRecordType, beneficiary_id: number) => {
  return async (dispatch: AppDispatch) => {
    try {
      const payload = getSanitizedBeneSchemaDetails(data);
      const response = await makeApiCall({
        method: 'patch',
        url: `${API_URL_V3}/remitter/beneficiary/${beneficiary_id}/payout/${beneficiary_id}`,
        withCredentials: true,
        data: payload
      });
      return response;
    } catch (error) {
      dispatch(handleError(error));
    }
  };
};
